/**
 *
 * TrialCountdownBanner
 *
 * A hardware buyer's 30-day Pro trial used to end in silence, and silence had
 * two shapes: a card on file was charged with no notice, and no card on file
 * auto-cancelled the subscription. The only prompt the dashboard ever showed
 * about either arrived AFTER access was already gone
 * (`components/ResubscribeMessage`). This is the prompt that arrives before.
 *
 * Persistent rather than dismissable, and slim rather than loud: it sits in the
 * content column for the whole trial, so it is answerable at any point instead
 * of being an interruption to clear. It renders nothing at all unless there is
 * a real number of days to name (see `selectTrialDaysRemaining`), which is what
 * keeps it off every non-trialing account's screen.
 *
 * It states the date and nothing else, deliberately. What happens AT the end of
 * the trial differs by customer — a card on file is charged, no card on file
 * auto-cancels — and this surface cannot tell which one it is talking to: the
 * session payload carries no `cclast4`. Copy that named either outcome would
 * therefore be false for half its readers, so naming the outcome is left to the
 * trial-ending email, which reads card state server-side and can be specific.
 * The one thing true for everyone — the day it ends — is the thing said here.
 */
import React from 'react';
import { Banner, Message, Cta } from './styledComponents';

/*
 * Days are spoken, not printed as a bare number, for the two values where a
 * numeral reads as wrong: "in 0 days" is not something anyone says, and "in 1
 * days" is the pluralisation bug every countdown ships at least once.
 */
const trialEndsCopy = (days) => {
  if (days === 0) return 'Your Pro trial ends today.';
  if (days === 1) return 'Your Pro trial ends tomorrow.';
  return `Your Pro trial ends in ${days} days.`;
};

/*
 * `daysRemaining` is whole days, or null/undefined when there is no honest
 * number — see `selectTrialDaysRemaining`, which `containers/App` reads and
 * passes down. Rendering nothing is handled HERE rather than at the call site
 * so every future caller inherits it: the one thing this banner must never do
 * is appear without a real day to name.
 */
const TrialCountdownBanner = ({ daysRemaining }) => {
  if (daysRemaining === null || daysRemaining === undefined) return null;

  return (
    // `role="status"` rather than a bare region: the banner appears when the
    // session resolves, a few hundred milliseconds after the page does, so a
    // screen reader that had already announced the page would otherwise never
    // mention it. Polite, so it waits its turn instead of cutting in.
    <Banner role="status">
      <Message>{trialEndsCopy(daysRemaining)}</Message>
      {/*
        * "Add payment method" would be the more pointed label, but it would
        * also be a promise the destination does not keep for everyone: the
        * Stripe portal this opens is equally where a customer updates a card
        * they already have, reads invoices, or cancels. The banner cannot tell
        * which of those a given reader needs (see above), so the label names
        * the destination rather than guessing the errand.
        */}
      <Cta>Manage subscription</Cta>
    </Banner>
  );
};

export default TrialCountdownBanner;
